import React from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { ModeEditOutlineTwoTone } from "@mui/icons-material";
import { toast } from "react-toastify";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import {
  useLazyListLegNotesQuery,
  useUpdateLegNoteMutation,
} from "store/services/legApi";

import FormDrawer from "components/Shared/FormDrawer";
import LegNoteForm from "./LegNoteForm";
import AppTable from "components/Shared/AppTable";
import { useTablePagination } from "hooks";

const LegNoteList = () => {
  const { id: legId } = useParams(); // assumes legId is from route param

  const [showNoteForm, setShowNoteForm] = React.useState(false);
  const [selectedNote, setSelectedNote] = React.useState(null);

  const { currentPage, handlePageChange, handlePerRowsChange } =
    useTablePagination({ perPage: 5 });
  const [perPage, setPerPage] = React.useState(5);

  // Columns for note table
  const columns = [
    {
      name: "Content",
      selector: (row) => row?.content,
      cell: (row) => row?.content,
    },
    {
      name: "Type",
      selector: (row) => row?.type,
      cell: (row) => row?.type,
    },
    {
      name: "Created By",
      selector: (row) => row?.createdBy?.name,
      cell: (row) => row?.createdBy?.name || "N/A",
    },
    {
      name: "Updated By",
      selector: (row) => row?.updatedBy?.name,
      cell: (row) => row?.updatedBy?.name || "N/A",
    },
    {
      name: "Actions",
      cell: (row) => (
        <IconButton
          size="small"
          aria-label="Edit Note"
          onClick={() => {
            setSelectedNote(row);
            setShowNoteForm(true);
          }}
        >
          <ModeEditOutlineTwoTone />
        </IconButton>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Hooks
  const [fetchNotes, { data: notesRes, isLoading, isFetching }] =
    useLazyListLegNotesQuery();
  const [updateNote, { isLoading: updatingNote }] =
    useUpdateLegNoteMutation();

  // Submit handler
  const handleUpdateNote = async (data) => {
    try {
      await updateNote({
        legId,
        noteId: data?.noteId,
        ...data,
      }).unwrap();
      toast.success("Note updated successfully!");
      handleCancelNote();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update note!");
    }
  };

  const handleCancelNote = () => {
    setShowNoteForm(false);
    setSelectedNote(null);
  };

  const handleRowsPerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    handlePerRowsChange(newPerPage);
  };

  React.useEffect(() => {
    fetchNotes({
      legId,
      page: currentPage,
      limit: perPage,
    });
  }, [currentPage, perPage, legId, fetchNotes]);

  return (
    <>
      <BreadcrumbLayout.Paper>
        {notesRes ? (
          <AppTable
            columns={columns}
            data={notesRes?.notes ?? []}
            progressPending={isLoading || isFetching}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={notesRes?.totalItems ?? 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPerPageChange}
          />
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding={2}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" marginLeft={1}>
              Loading Notes...
            </Typography>
          </Box>
        )}
      </BreadcrumbLayout.Paper>

      <FormDrawer
        title="Edit Note"
        open={showNoteForm}
        onClose={handleCancelNote}
      >
        <LegNoteForm
          isBusy={updatingNote}
          note={selectedNote}
          onSubmit={handleUpdateNote}
          onCancel={handleCancelNote}
        />
      </FormDrawer>
    </>
  );
};

export default LegNoteList;
